import Reveal, { SectionLabel } from "@/components/Reveal";
import { Send, CalendarDays, MessagesSquare, Mic2, Pin, Lock } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const PERKS = [
  { icon: CalendarDays, title: "Daily Market Blueprints", desc: "Key zones, bias and scenarios for XAUUSD and major pairs — mapped before the session opens." },
  { icon: Mic2, title: "Live Weekly Webinars", desc: "Chart breakdowns, Q&A and trade reviews in real time with the Atlas team." },
  { icon: MessagesSquare, title: "Peer Analysis Chat", desc: "A serious room of traders sharing markups and feedback — no hype, no spam, no signal-chasing." },
];

const FEED = [
  { initials: "JM", name: "James M.", tag: "Member · UK", text: "Markup for today: sweep of Asia low into the H4 buy zone. Waiting for displacement before I touch anything.", pinned: true },
  { initials: "LK", name: "Lena K.", tag: "Member · Germany", text: "Sat out the news spike. Old me would have chased it. Journaled it instead — small win." },
  { initials: "RA", name: "Rayan A.", tag: "Member · UAE", text: "Webinar notes posted. The order flow section finally made FVGs click for me." },
];

const Community = () => (
  <section id="community" className="relative py-24 lg:py-36" data-testid="community-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-6">
        <Reveal>
          <SectionLabel>The Community</SectionLabel>
          <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50 leading-tight" data-testid="community-title">
            Start free.
            <span className="text-gold-gradient"> Go exclusive.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-slate-400 leading-relaxed max-w-lg">
            The free Atlas community is open to everyone — market talk, weekly takeaways and a feel for how we work.
            Members unlock the exclusive rooms: early analysis insights, deeper breakdowns and daily education.
          </p>
        </Reveal>

        <div className="mt-9 space-y-6">
          {PERKS.map((p, i) => (
            <Reveal key={p.title} delay={0.1 * i}>
              <div className="flex gap-4 items-start" data-testid={`community-perk-${i + 1}`}>
                <span className="mt-1 w-10 h-10 shrink-0 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                  <p.icon size={17} className="text-gold" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base sm:text-lg text-slate-100">{p.title}</h3>
                  <p className="mt-1 text-sm text-slate-400 leading-relaxed max-w-md">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3}>
          <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="community-tiers">
            <div className="border border-line rounded-md p-5 bg-card2/40" data-testid="community-tier-free">
              <p className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Free Community</p>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                Open to all. Market discussion, weekly takeaways and a taste of the Atlas approach.
              </p>
              <a
                href="https://t.me/addlist/O9NU4HFXS-cxYzNk"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="community-telegram-link"
                className="mt-4 inline-flex items-center gap-2 border border-gold/40 text-gold font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <Send size={14} /> Join Free on Telegram
              </a>
            </div>
            <div className="border border-gold/40 rounded-md p-5 bg-gold/5 gold-ring" data-testid="community-tier-exclusive">
              <p className="font-mono text-[10px] tracking-[0.25em] text-gold uppercase flex items-center gap-2">
                <Lock size={11} /> Exclusive Rooms
              </p>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                Members only. Early analysis insights, deeper breakdowns and priority Q&A — unlocked with the Lifetime Access Pass.
              </p>
              <button
                onClick={() => scrollToSection("join")}
                data-testid="community-exclusive-cta"
                className="mt-4 inline-flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-gold-bright transition-colors duration-300"
              >
                Unlock With Atlas
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-6">
        <Reveal delay={0.15}>
          <div className="glass-panel border border-line chart-frame p-4 sm:p-6" data-testid="community-feed">
            <div className="flex items-center justify-between mb-4 px-1">
              <span className="font-mono text-[11px] tracking-[0.25em] text-slate-300">REAL MARKUPS · SHARED TO THE GROUP</span>
              <span className="font-mono text-[10px] tracking-widest text-gold/80">DRAWN BY THE FOUNDER</span>
            </div>
            <video
              src="/atlas-boxes-markup.mp4"
              poster="/atlas-boxes-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-auto rounded-sm border border-line/60"
              data-testid="community-boxes-video"
            />
            <p className="mt-4 px-1 text-[11px] text-slate-500">
              The same hand-drawn zones members receive in the chat — marked out before price gets there.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Community;
